import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../services/api';

type TokenState = 'loading' | 'valid' | 'used' | 'expired' | 'invalid';

const ResetPassword: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<TokenState>('loading');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setState('invalid');
        return;
      }
      try {
        const response = await authAPI.verifyResetToken(token);
        if (response.status === 'valid') {
          setState('valid');
        } else if (response.status === 'used') {
          setState('used');
        } else if (response.status === 'expired') {
          setState('expired');
        } else {
          setState('invalid');
        }
      } catch (err: any) {
        const status = err?.response?.data?.status;
        if (status === 'used') {
          setState('used');
        } else if (status === 'expired') {
          setState('expired');
        } else {
          setState('invalid');
        }
      }
    };

    verifyToken();
  }, [token]);

  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => {
      navigate('/login', { state: { message: success } });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [success, navigate]);

  const passwordStrengthError = (value: string) => {
    if (value.length < 8) return 'Password must be at least 8 characters.';
    if (!/[a-z]/.test(value)) return 'Password must include a lowercase letter.';
    if (!/[A-Z]/.test(value)) return 'Password must include an uppercase letter.';
    if (!/[0-9]/.test(value)) return 'Password must include a number.';
    if (!/[^A-Za-z0-9]/.test(value)) return 'Password must include a special character.';
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    const strengthError = passwordStrengthError(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await authAPI.resetPassword({
        token,
        password,
        confirm_password: confirmPassword
      });
      setSuccess(response.message || 'Password has been successfully reset.');
      setState('used');
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to reset password.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="container center">
        <div className="card">
          <p className="muted">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container center">
        <div className="card" style={{ maxWidth: '420px', width: '100%' }}>
          <h2 className="h2">Password reset</h2>
          <div className="alert success">{success}</div>
          <p className="muted">Redirecting you to login...</p>
          <Link to="/login" className="btn" style={{ marginTop: '12px' }}>
            Go to login now
          </Link>
        </div>
      </div>
    );
  }

  if (state !== 'valid') {
    const message = state === 'used'
      ? 'This reset link has already been used.'
      : state === 'expired'
        ? 'This reset link has expired.'
        : 'This reset link is invalid.';

    return (
      <div className="container center">
        <div className="card">
          <h2 className="h2">Reset link unavailable</h2>
          <p className="muted">{message}</p>
          <Link to="/login" className="btn" style={{ marginTop: '12px' }}>
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="center">
        <div className="card shadow" style={{ maxWidth: '420px', width: '100%' }}>
          <h1 className="h2">Reset password</h1>
          <p className="muted">Enter a new password for your account.</p>

          {success && <div className="alert success">{success}</div>}
          {error && <div className="alert error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">New password</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Confirm password</label>
              <input
                type="password"
                className="input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn" disabled={saving} style={{ width: '100%' }}>
              {saving ? 'Resetting...' : 'Reset password'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Link to="/login">Back to login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
